import React, { useState } from 'react';
import { Calendar, ChevronDown, ExternalLink } from 'lucide-react';
import { useI18n } from '@/lib/i18n';
import { useIsMobile } from '@/hooks/use-mobile';
import { googleCalendarUrl, buildICS } from '@/lib/calendar';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import {
  Drawer, DrawerTrigger, DrawerContent, DrawerHeader, DrawerTitle,
} from '@/components/ui/drawer';

function downloadICS(content, filename) {
  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function AddToCalendar({ show, hostName, className = '' }) {
  const { lang } = useI18n();
  const es = lang === 'es';
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);

  const gcal = googleCalendarUrl(show, lang, hostName);
  const ics = buildICS(show, lang, hostName);
  if (!gcal || !ics) return null;

  const label = es ? 'Agregar al calendario' : 'Add to calendar';

  const MenuItems = () => (
    <>
      <a
        href={gcal}
        target="_blank"
        rel="noreferrer"
        onClick={() => setOpen(false)}
        className="flex items-center justify-between px-3 py-2.5 text-sm font-semibold text-neutral-700 hover:bg-purple-50 hover:text-purple-700 transition-colors"
      >
        Google Calendar
        <ExternalLink size={14} className="text-neutral-400" />
      </a>
      <button
        onClick={() => {
          downloadICS(ics, `${show.slug || 'show'}.ics`);
          setOpen(false);
        }}
        className="w-full flex items-center justify-between px-3 py-2.5 text-sm font-semibold text-neutral-700 hover:bg-purple-50 hover:text-purple-700 transition-colors text-left"
      >
        Apple / Outlook (.ics)
        <ExternalLink size={14} className="text-neutral-400" />
      </button>
    </>
  );

  const triggerBtn = (
    <button
      className={`inline-flex items-center gap-1.5 py-1.5 text-xs font-semibold text-neutral-600 hover:text-purple-700 transition-colors whitespace-nowrap ${className}`}
    >
      <Calendar size={14} />
      {label}
      <ChevronDown size={12} />
    </button>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerTrigger asChild>{triggerBtn}</DrawerTrigger>
        <DrawerContent>
          <DrawerHeader className="pb-2">
            <DrawerTitle className="text-base font-bold text-neutral-900">{label}</DrawerTitle>
          </DrawerHeader>
          <div className="pb-4 px-1">
            <MenuItems />
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{triggerBtn}</PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        collisionPadding={8}
        className="w-56 p-1 rounded-lg border border-neutral-200 shadow-xl"
      >
        <MenuItems />
      </PopoverContent>
    </Popover>
  );
}